import { FC, useEffect, useId } from "react";

import { RECENT_MESSAGE_HOURS } from "components/chat/constants";
import { SecondaryOverlay } from "components/constants";
import { t } from "i18n/i18n";
import { ButtonBadge } from "mds/components/ButtonBadge";
import { IconText } from "mds/components/IconText";
import { MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ChatStatusIcon, ChatWithLinesFilledIcon, ChatWithLinesIcon } from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectUnreadChatMessageCountForCurrentSection,
  selectUnseenChatMentionMessageIdsForCurrentSection,
} from "store/selectors";
import { chatMentionsActions } from "store/slices/chat-mentions";
import { useOverlayControls } from "utils/routing/hooks/use-overlay-controls";

export const ChatPanelToggleButton: FC<{ isMobile?: boolean; isMenuItem?: boolean }> = ({
  isMobile = false,
  isMenuItem = false,
}) => {
  const { isSelected, togglePanel } = useOverlayControls(SecondaryOverlay.ClassChat);
  const dispatch = useAppDispatch();
  const unreadChatMessageCount = useAppSelector(selectUnreadChatMessageCountForCurrentSection);
  const unseenMentionMessageIds = useAppSelector(
    selectUnseenChatMentionMessageIdsForCurrentSection,
  );
  const sectionId = useAppSelector(selectCurrentSectionId);
  const hasUnseenMention = unseenMentionMessageIds.length > 0;
  const classChatLabel = t("content_sidebar.title.ClassChat");
  const mentionStatusDescriptionId = useId();
  const mentionStatusDescription = hasUnseenMention ? (
    <span className="sr-only" id={mentionStatusDescriptionId}>
      {t("chat.unseen_mention_status")}
    </span>
  ) : null;

  useEffect(() => {
    if (sectionId) {
      storeApi.chat_messages.list({
        section_id: sectionId,
        created_at__within: `${RECENT_MESSAGE_HOURS}h`,
      });
    }
  }, [sectionId]);

  const icon = hasUnseenMention ? (
    <ChatStatusIcon statusColor="orange" />
  ) : unreadChatMessageCount ? (
    <ChatWithLinesFilledIcon />
  ) : (
    <ChatWithLinesIcon />
  );
  const handleClick = () => {
    if (!hasUnseenMention || !sectionId) {
      togglePanel();
      return;
    }

    dispatch(chatMentionsActions.requestOldestUnseenMention({ sectionId }));
    if (!isSelected) togglePanel();
  };

  if (isMenuItem) {
    return (
      <MenuItem
        aria-describedby={hasUnseenMention ? mentionStatusDescriptionId : undefined}
        aria-label={classChatLabel}
        eventName="Class Chat - Toggle"
        eventProps={{ isOpened: !isSelected }}
        onClick={handleClick}
      >
        <IconText iconStart={icon} text={classChatLabel} />
        {mentionStatusDescription}
      </MenuItem>
    );
  }

  return (
    <>
      <SquareIconButton
        aria-describedby={hasUnseenMention ? mentionStatusDescriptionId : undefined}
        aria-label={classChatLabel}
        className="relative"
        eventName="Class Chat - Toggle"
        eventProps={{ isOpened: !isSelected }}
        isSelected={isSelected}
        kind="secondary"
        size={isMobile ? "xl" : "l"}
        onClick={handleClick}
      >
        {icon}
        {!hasUnseenMention && Boolean(unreadChatMessageCount) && (
          <ButtonBadge>{unreadChatMessageCount}</ButtonBadge>
        )}
      </SquareIconButton>
      {mentionStatusDescription}
    </>
  );
};
